<template>
  <div>
    <!-- Until the stores and the parade year are in -->
    <div
      v-if="dataLoading"
      class="fixed inset-0 z-50 grid place-items-center bg-[var(--kb-scrim)]"
    >
      <p
        class="kb-pane m-0 !py-3 font-mono text-[13px] text-kb-muted"
        role="status"
      >
        loading the krewe<span
          class="kb-caret"
          aria-hidden="true"
        />
      </p>
    </div>
    <app-dashboard>
      <!-- Main content slot -->
      <slot />

      <!-- Add drawer content slot -->
      <template #drawer-content="{ data }">
        <slot
          name="drawer-content"
          :data="data"
        />
      </template>
    </app-dashboard>

    <!-- Add SubscriptionDebugPanel -->
    <DevSubscriptionDebugPanel />

    <!-- Chatbot Widget -->
    <ChatbotWidget />

    <!-- Admin Command Palette (Cmd+K) -->
    <AdminCommandPalette v-if="isAnyAdmin" />

    <!-- Membership dues reminder: pay, redeem a code, or join a pre-approved
         sponsored SubKrewe / band without paying -->
    <Dialog
      v-model:visible="showDuesReminderDialog"
      modal
      header="Membership dues"
      :style="{ width: '32rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :draggable="false"
    >
      <div class="kb-form">
        <!-- Sponsored SubKrewe fast path -->
        <template v-if="showSponsoredFastPath">
          <p class="kb-dialog-lead">
            <template v-if="sponsoredPreapprovedSubkrewes.length === 1">
              You have been pre-approved for the sponsored SubKrewe
              <strong>{{ sponsoredPreapprovedSubkrewes[0]?.name }}</strong>. Join now to skip paying membership dues.
            </template>
            <template v-else>
              You have been pre-approved for multiple sponsored SubKrewes. Pick one and join now to skip paying membership dues.
            </template>
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <Select
              v-if="sponsoredPreapprovedSubkrewes.length > 1"
              v-model="selectedSponsoredSubkreweId"
              :options="sponsoredPreapprovedSubkrewes"
              option-label="name"
              option-value="id"
              placeholder="Pick a sponsored SubKrewe"
              aria-label="Sponsored SubKrewe"
              size="small"
              class="min-w-0 flex-1 basis-56"
            />
            <Button
              :disabled="(sponsoredPreapprovedSubkrewes.length > 1 && !selectedSponsoredSubkreweId) || joinInProgress"
              size="small"
              :label="joinInProgress ? 'Joining…' : sponsoredPreapprovedSubkrewes.length === 1 ? `Join ${sponsoredPreapprovedSubkrewes[0]?.name}` : 'Join'"
              @click="joinSelectedSponsored()"
            />
            <Button
              v-if="sponsoredPreapprovedSubkrewes.length > 1 && selectedSponsoredSubkreweId"
              size="small"
              text
              severity="secondary"
              label="View details"
              @click="navigateToSubkrewe(selectedSponsoredSubkreweId)"
            />
          </div>
          <div class="kb-divider">
            or
          </div>
        </template>

        <!-- Band fast path -->
        <template v-if="showBandFastPath">
          <p class="kb-dialog-lead">
            <template v-if="bandPreapprovedSubkrewes.length === 1">
              You have been pre-approved for the band
              <strong>{{ bandPreapprovedSubkrewes[0]?.name }}</strong>. Join now to skip paying membership dues.
            </template>
            <template v-else>
              You have been pre-approved for multiple bands. Pick one and join now to skip paying membership dues.
            </template>
          </p>
          <div class="flex flex-wrap items-center gap-2">
            <Select
              v-if="bandPreapprovedSubkrewes.length > 1"
              v-model="selectedBandSubkreweId"
              :options="bandPreapprovedSubkrewes"
              option-label="name"
              option-value="id"
              placeholder="Pick a band"
              aria-label="Band"
              size="small"
              class="min-w-0 flex-1 basis-56"
            />
            <Button
              :disabled="(bandPreapprovedSubkrewes.length > 1 && !selectedBandSubkreweId) || joinBandInProgress"
              size="small"
              :label="joinBandInProgress ? 'Joining…' : bandPreapprovedSubkrewes.length === 1 ? `Join ${bandPreapprovedSubkrewes[0]?.name}` : 'Join'"
              @click="joinSelectedBand()"
            />
            <Button
              v-if="bandPreapprovedSubkrewes.length > 1 && selectedBandSubkreweId"
              size="small"
              text
              severity="secondary"
              label="View details"
              @click="navigateToSubkrewe(selectedBandSubkreweId)"
            />
          </div>
          <div class="kb-divider">
            or
          </div>
        </template>

        <p class="kb-dialog-lead">
          You need to pay your membership dues before joining a SubKrewe. Pay now to unlock all member benefits.
        </p>
        <p class="kb-dialog-lead">
          If you received a code from your SubKrewe captain, choose <strong>Redeem code</strong> to apply it instead of paying. You will be added to the SubKrewe you received the code from.
        </p>
        <p class="kb-field-hint">
          Payments are handled by Stripe. You'll get an order confirmation email, but <strong>no</strong> printable tickets: at wristband pickup you show your Krewebacchus QR code.
          Memberships of the Intergalactic Krewe of Chewbacchus are non-refundable.
        </p>
      </div>

      <template #footer>
        <Button
          label="Maybe later"
          text
          severity="secondary"
          size="small"
          class="mr-auto"
          @click="showDuesReminderDialog = false"
        />
        <SharedRedeemCodeLauncher
          button-size="small"
          @redeemed="showDuesReminderDialog = false"
        />
        <SharedPurchaseButton
          ticket-type="membership"
          button-size="small"
          @checkout-closed="showDuesReminderDialog = false"
        />
      </template>
    </Dialog>

    <!-- Children tickets reminder -->
    <Dialog
      v-model:visible="showChildrenTicketsDialog"
      modal
      header="Children's tickets"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :draggable="false"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          You have <strong>{{ childrenWithoutTickets.length }}</strong>
          {{ childrenWithoutTickets.length === 1 ? 'child' : 'children' }}
          registered who {{ childrenWithoutTickets.length === 1 ? 'doesn\'t' : 'don\'t' }} have a parade ticket yet.
          Buy tickets now so they can march with you on parade day.
        </p>
        <p class="kb-field-hint">
          Children's tickets are separate from membership dues.
        </p>
      </div>

      <template #footer>
        <Button
          label="Maybe later"
          text
          severity="secondary"
          size="small"
          @click="showChildrenTicketsDialog = false"
        />
        <SharedPurchaseButton
          ticket-type="children"
          button-label="Buy children's tickets"
          icon="pi pi-ticket"
          button-size="small"
          :children-ids="childrenWithoutTickets"
          @checkout-closed="showChildrenTicketsDialog = false"
        />
      </template>
    </Dialog>

    <!-- Find a SubKrewe (dues paid, no SubKrewe yet) -->
    <Dialog
      v-model:visible="showFindSubkreweDialog"
      modal
      header="Find your SubKrewe"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :draggable="false"
      @hide="findSubkreweDialogDismissed = true"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          <i
            class="pi pi-check text-kb-accent mr-1"
            aria-hidden="true"
          /><strong>Your membership dues are paid.</strong>
        </p>
        <p class="kb-dialog-lead">
          Now find a SubKrewe to join: the smaller groups within the Krewe that you'll parade with.
        </p>
      </div>

      <template #footer>
        <Button
          label="Maybe later"
          text
          severity="secondary"
          size="small"
          @click="showFindSubkreweDialog = false; findSubkreweDialogDismissed = true"
        />
        <Button
          label="Find a SubKrewe"
          icon="pi pi-search"
          size="small"
          @click="navigateToFindSubkrewe"
        />
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useCurrentUser } from 'vuefire'
import { useMemberPreapprovals } from '~/composables/useMemberPreapprovals.js'
import { useChildrenTickets } from '~/composables/useChildrenTickets.js'
import { memberIsAdmin, memberIsBandAdmin, memberIsRedshirtAdmin } from '~/shared/utils/memberUtils.js'
import { useAnnouncement } from '~/composables/useAnnouncement'

const systemConfigStore = useSystemConfigStore()
// Show loading until system config (parade year) is loaded; pages show their
// own while their live data arrives
const dataLoading = computed(() => !systemConfigStore.loaded)
const route = useRoute()
const router = useRouter()
const toast = useToast()

// Announcement system
const { triggerIfNeeded: triggerAnnouncement } = useAnnouncement()

// Payment result handling (shows toast on return from Stripe checkout)
const { handlePaymentResult } = usePaymentResult()

onMounted(() => {
  setupDuesDialogLogic()
  handlePaymentResult()
})

// Dialog state
const showDuesReminderDialog = ref(false)
const showChildrenTicketsDialog = ref(false)
const showFindSubkreweDialog = ref(false)
const findSubkreweDialogDismissed = ref(false)

// Use the centralized shouldPayDues composable
const shouldPayDues = useShouldPayDues()

// Use children tickets composable
const { childrenWithoutTickets, hasChildrenWithoutTickets } = useChildrenTickets()

// Pre-approved sponsored SubKrewe fast path (centralized)
const { load: loadPreapprovals, sponsoredPreapprovedSubkrewes, bandPreapprovedSubkrewes } = useMemberPreapprovals()
const subkrewesStore = useSubKrewesStore()
const membersStore = useMembersStore()
const tasksStore = useTasksStore()
const currentUser = useCurrentUser()
const selectedSponsoredSubkreweId = ref<string | null>(null)
const selectedBandSubkreweId = ref<string | null>(null)
const joinInProgress = ref(false)
const joinBandInProgress = ref(false)
const memberPath = computed(() => currentUser.value?.uid ? `members/${currentUser.value.uid}` : null)
const memberData = computed<Member | null>(() => memberPath.value ? (membersStore.members[memberPath.value] || null) : null)

// Track whether required dialogs (shown in app.vue) are blocking the dues dialog
const memberNeedsNameUpdate = computed(() => {
  if (!memberData.value) return false
  const { first_name, last_name } = memberData.value
  return !first_name?.trim() || !last_name?.trim()
})
const memberNeedsEmergencyContact = computed(() => {
  if (!memberData.value) return false
  if (!memberData.value.member_type || memberData.value.member_type === 'pending') return false
  const { emergency_contact, emergency_contacts } = memberData.value
  if (emergency_contacts && emergency_contacts.length > 0) {
    // A missing first contact counts as incomplete, same as a blank one.
    const first = emergency_contacts[0]
    return !first?.name?.trim() || !first?.phone?.trim()
  }
  return !emergency_contact?.name?.trim() || !emergency_contact?.phone?.trim()
})
const memberNeedsRequiredDialog = computed(() => memberNeedsNameUpdate.value || memberNeedsEmergencyContact.value)

// Admin check for command palette
const isAnyAdmin = computed(() => {
  return memberIsAdmin(memberData.value) || memberIsBandAdmin(memberData.value) || memberIsRedshirtAdmin(memberData.value)
})
const hasPendingJoinRequest = computed(() => {
  return tasksStore.tasksArray.some((task) => {
    if (task.taskImplementation !== 'SubKreweJoinRequestTask') return false
    if (task.metadata?.memberId !== memberData.value?.id) return false
    if (task.status !== 'pending' && task.status !== 'in_progress') return false
    return true
  })
})
const hasPaidButNoSubkrewe = computed(() => {
  // User has paid (shouldPayDues is false) but has no subkrewe
  // Don't show if they have a pending join request
  if (hasPendingJoinRequest.value) return false
  return !shouldPayDues.value && memberData.value && !memberData.value.subkrewe?.id
})
const showSponsoredFastPath = computed(() => {
  const list = sponsoredPreapprovedSubkrewes?.value
  return shouldPayDues.value && Array.isArray(list) && list.length > 0
})
const showBandFastPath = computed(() => {
  const list = bandPreapprovedSubkrewes?.value
  return shouldPayDues.value && Array.isArray(list) && list.length > 0
})

// (Removed unused sponsoredFastPathText after design change)

watch(sponsoredPreapprovedSubkrewes, (list) => {
  const only = Array.isArray(list) && list.length === 1 ? list[0] : undefined
  if (only) selectedSponsoredSubkreweId.value = only.id
}, { immediate: true })

watch(bandPreapprovedSubkrewes, (list) => {
  const only = Array.isArray(list) && list.length === 1 ? list[0] : undefined
  if (only) selectedBandSubkreweId.value = only.id
}, { immediate: true })

async function joinSelectedSponsored() {
  const targetId = selectedSponsoredSubkreweId.value
  if (!targetId) return
  // Pinia getter getSubkreweById returns a function; when accessed via store it is already the inner function
  const subGetter = subkrewesStore.getSubkreweById as unknown as (id: string) => SubKrewe | undefined
  const sub = subGetter ? subGetter(targetId) : undefined
  if (!sub || !memberData.value) return
  try {
    joinInProgress.value = true
    const ok = await subkrewesStore.addMemberToSubKrewe(sub, memberData.value as Member)
    if (ok) {
      toast.add({ severity: 'success', summary: 'Joined', detail: `You joined ${sub.name}.`, life: 3000 })
      showDuesReminderDialog.value = false
    }
    else {
      toast.add({ severity: 'error', summary: 'Failed', detail: 'Could not join SubKrewe.', life: 3000 })
    }
  }
  catch (e) {
    console.error('[default] join sponsored error', e)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Unexpected error joining.', life: 3000 })
  }
  finally {
    joinInProgress.value = false
  }
}

async function joinSelectedBand() {
  const targetId = selectedBandSubkreweId.value
  if (!targetId) return
  const subGetter = subkrewesStore.getSubkreweById as unknown as (id: string) => SubKrewe | undefined
  const sub = subGetter ? subGetter(targetId) : undefined
  if (!sub || !memberData.value) return
  try {
    joinBandInProgress.value = true
    const ok = await subkrewesStore.addMemberToSubKrewe(sub, memberData.value as Member)
    if (ok) {
      toast.add({ severity: 'success', summary: 'Joined', detail: `You joined ${sub.name}.`, life: 3000 })
      showDuesReminderDialog.value = false
    }
    else {
      toast.add({ severity: 'error', summary: 'Failed', detail: 'Could not join band.', life: 3000 })
    }
  }
  catch (e) {
    console.error('[default] join band error', e)
    toast.add({ severity: 'error', summary: 'Error', detail: 'Unexpected error joining.', life: 3000 })
  }
  finally {
    joinBandInProgress.value = false
  }
}

function navigateToSubkrewe(_id: string) {}

function navigateToFindSubkrewe() {
  showFindSubkreweDialog.value = false
  findSubkreweDialogDismissed.value = true
  router.push('/subkrewe')
}

// Setup dialog logic and watch for route changes
const setupDuesDialogLogic = () => {
  // Watch for route changes, shouldPayDues changes, and required dialog dismissals
  watch(
    () => [route.path, shouldPayDues.value, currentUser.value, memberNeedsRequiredDialog.value],
    ([_path, shouldPay, user, needsRequiredDialog]) => {
      // Don't show dialogs if user is logging out (no current user)
      if (!user) {
        showDuesReminderDialog.value = false
        return
      }

      // If user has paid, close the dialog
      if (!shouldPay) {
        showDuesReminderDialog.value = false
        return
      }

      // Don't show dues dialog while a required dialog (name/emergency contact) is blocking
      if (needsRequiredDialog) {
        showDuesReminderDialog.value = false
        return
      }

      showDuesReminderDialog.value = true
      // Lazy load preapproved sponsored and band options when dialog appears, ensuring subkrewes loaded
      ;(async () => {
        try {
          const subStore = useSubKrewesStore()
          await subStore.ensureSubKrewesLoaded?.()
          await loadPreapprovals()
          const subGetter = subStore.getSubkreweById as unknown as (id: string) => SubKrewe | undefined

          // Fetch sponsored subkrewes
          for (const p of sponsoredPreapprovedSubkrewes.value) {
            if (!subGetter?.(p.id)) {
              try {
                await subStore.fetchSubKrewe(p.id)
              }
              catch {
                /* noop */
              }
            }
          }
          if (sponsoredPreapprovedSubkrewes.value.length === 1) {
            selectedSponsoredSubkreweId.value = sponsoredPreapprovedSubkrewes.value[0]?.id ?? null
          }

          // Fetch band subkrewes
          for (const p of bandPreapprovedSubkrewes.value) {
            if (!subGetter?.(p.id)) {
              try {
                await subStore.fetchSubKrewe(p.id)
              }
              catch {
                /* noop */
              }
            }
          }
          if (bandPreapprovedSubkrewes.value.length === 1) {
            selectedBandSubkreweId.value = bandPreapprovedSubkrewes.value[0]?.id ?? null
          }
        }
        catch (e) {
          console.error('[default] fast-path preload error', e)
        }
      })()
    },
    { immediate: true },
  )

  // Watch for children tickets dialog - show after dues are paid but children tickets aren't
  // Include route.path to re-show on navigation (like dues dialog does for unpaid dues)
  watch(
    () => [route.path, shouldPayDues.value, hasChildrenWithoutTickets.value, currentUser.value, memberNeedsRequiredDialog.value],
    ([_path, shouldPay, hasUnpaidChildren, user, needsRequiredDialog]) => {
      // Don't show dialogs if user is logging out (no current user)
      if (!user) {
        showChildrenTicketsDialog.value = false
        return
      }
      // A required dialog (name/emergency contact) goes first
      if (needsRequiredDialog) {
        showChildrenTicketsDialog.value = false
        return
      }
      // Only show if: dues paid and has unpaid children
      if (!shouldPay && hasUnpaidChildren) {
        showChildrenTicketsDialog.value = true
      }
    },
    { immediate: true },
  )

  // Watch for find subkrewe dialog - show when member has paid but no subkrewe
  // Only show once per session (until dismissed)
  watch(
    () => [hasPaidButNoSubkrewe.value, currentUser.value, memberNeedsRequiredDialog.value, route.path],
    ([shouldShow, user, needsRequiredDialog, path]) => {
      // Don't show dialogs if user is logging out (no current user)
      if (!user) {
        showFindSubkreweDialog.value = false
        return
      }
      // A required dialog (name/emergency contact) goes first
      if (needsRequiredDialog) {
        showFindSubkreweDialog.value = false
        return
      }
      // Not on the pages it would send them to
      if (path === '/subkrewe' || path === '/register-subkrewe') {
        showFindSubkreweDialog.value = false
        return
      }
      // Only show if not already dismissed this session and condition is met
      if (shouldShow && !findSubkreweDialogDismissed.value && !showDuesReminderDialog.value) {
        showFindSubkreweDialog.value = true
      }
      else if (!shouldShow) {
        // Member now has a subkrewe, close the dialog
        showFindSubkreweDialog.value = false
      }
    },
    { immediate: true },
  )

  // Watch for announcement popup - show after dues dialog closes (or immediately if no dues dialog)
  watch(
    () => [showDuesReminderDialog.value, currentUser.value],
    ([duesDialogVisible, user]) => {
      if (!user) return

      // If dues dialog just closed (or never showed), trigger announcement
      if (!duesDialogVisible) {
        // Small delay to let the dues dialog animate out
        setTimeout(() => {
          triggerAnnouncement()
        }, 300)
      }
    },
    { immediate: true },
  )
}
</script>

<style>

</style>
